import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPatch, apiPost } from '~/lib/api';

/**
 * Bookings — filterable table with inline status/notes editing (VV pattern).
 * Party/dates/totals/provider refs are read-only: sync + the engine own them.
 * Status labels use Adam's lingo: Hooked / On Hold / Released / Fished.
 */

export interface BookingListRow {
  id: string;
  code: string;
  provider: string;
  source: string;
  status: string;
  provider_status: string | null;
  trip_date: string | null;
  start_time: string;
  starts_at: string;
  num_adults: number;
  num_children: number;
  total_cents: number;
  deposit_cents: number;
  balance_due_cents: number;
  balance_status: string;
  currency: string;
  notes: string | null;
  internal_notes: string | null;
  booked_at: string;
  trip_name: string | null;
  trip_slug: string | null;
  customer_email: string | null;
  customer_first: string | null;
  customer_last: string | null;
  customer_phone: string | null;
}

const STATUS_TABS: Array<{ key: string; label: string }> = [
  { key: '', label: 'All' },
  { key: 'confirmed', label: 'Hooked' },
  { key: 'hold', label: 'On Hold' },
  { key: 'pending', label: 'Pending' },
  { key: 'cancelled', label: 'Released' },
  { key: 'completed', label: 'Fished' },
  { key: 'no_show', label: 'No show' },
];

const STATUS_OPTIONS = ['pending', 'confirmed', 'cancelled', 'completed', 'no_show'];

const money = (cents: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(
    cents / 100,
  );

/** Exact to the cent — anything money-actionable must not round. */
const moneyExact = (cents: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);

const prettyTime = (hhmm: string) => {
  if (!hhmm) return '';
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

export function Bookings() {
  const [items, setItems] = useState<BookingListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { status: string; internal_notes: string }>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ per_page: '100' });
    if (status) params.set('status', status);
    if (search) params.set('q', search);
    apiGet<{ items: BookingListRow[]; total: number }>(`/api/admin/bookings?${params}`)
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
        setDrafts({});
        setError(null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [status, search]);

  useEffect(load, [load]);

  const draftFor = (b: BookingListRow) =>
    drafts[b.id] ?? { status: b.status, internal_notes: b.internal_notes ?? '' };

  const isDirty = (b: BookingListRow) => {
    const d = draftFor(b);
    return d.status !== b.status || d.internal_notes !== (b.internal_notes ?? '');
  };

  const setDraft = (id: string, patch: Partial<{ status: string; internal_notes: string }>) => {
    setDrafts((prev) => {
      const row = items.find((i) => i.id === id)!;
      const base = prev[id] ?? { status: row.status, internal_notes: row.internal_notes ?? '' };
      return { ...prev, [id]: { ...base, ...patch } };
    });
  };

  /** Merge fresh row data in place — never wipes other rows' unsaved drafts. */
  const mergeRow = (id: string, patch: Partial<BookingListRow>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    setDrafts((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const save = async (b: BookingListRow) => {
    const d = draftFor(b);
    setBusy(b.id);
    try {
      const res = await apiPatch<{ booking: Partial<BookingListRow> }>(`/api/admin/bookings/${b.id}`, {
        ...(d.status !== b.status ? { status: d.status } : {}),
        ...(d.internal_notes !== (b.internal_notes ?? '') ? { internal_notes: d.internal_notes } : {}),
      });
      mergeRow(b.id, res.booking);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const chargeBalance = async (b: BookingListRow) => {
    if (!window.confirm(`Charge the ${moneyExact(b.balance_due_cents, b.currency)} balance on ${b.code} now?`)) return;
    setBusy(b.id);
    try {
      await apiPost(`/api/admin/bookings/${b.id}/charge-balance`, {});
      const detail = await apiGet<{ booking: Partial<BookingListRow> }>(`/api/admin/bookings/${b.id}`);
      mergeRow(b.id, detail.booking);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="trc-page">
      <header className="trc-page-header">
        <p className="trc-eyebrow">Operations</p>
        <h1 className="trc-osw trc-page-title">Bookings</h1>
      </header>

      <div className="trc-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            className={`trc-tab${status === t.key ? ' trc-tab--active' : ''}`}
            onClick={() => setStatus(t.key)}
          >
            {t.label}
          </button>
        ))}
        <form
          className="trc-tab-search"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(q.trim());
          }}
        >
          <input
            placeholder="Search code, name, email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </form>
      </div>

      {loading && <p>Loading…</p>}
      {error && <p className="trc-error">Error: {error}</p>}
      {!loading && !error && items.length === 0 && (
        <p className="trc-page-empty">
          No bookings{status ? ` with this status` : ' yet'}. Native + Checkfront checkouts and
          manual entries land here.
        </p>
      )}

      {items.length > 0 && (
        <div className="trc-table-wrap">
          <table className="trc-table">
            <thead>
              <tr>
                <th>Trip</th>
                <th>Guest</th>
                <th>Party</th>
                <th>Money</th>
                <th>Engine</th>
                <th>Status</th>
                <th>Internal notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => {
                const d = draftFor(b);
                const guestName =
                  [b.customer_first, b.customer_last].filter(Boolean).join(' ') || '—';
                const paid =
                  b.balance_status === 'paid' || (b.status === 'confirmed' && b.balance_due_cents === 0);
                return (
                  <tr key={b.id} className={b.status === 'cancelled' ? 'trc-row--muted' : ''}>
                    <td>
                      <strong>{b.trip_name ?? '—'}</strong>
                      <div className="trc-cell-sub">
                        {b.trip_date} {prettyTime(b.start_time)} · {b.code}
                      </div>
                    </td>
                    <td>
                      {guestName}
                      <div className="trc-cell-sub">
                        {b.customer_email}
                        {b.customer_phone ? ` · ${b.customer_phone}` : ''}
                      </div>
                    </td>
                    <td>
                      {b.num_adults + b.num_children}
                      {b.num_children > 0 && (
                        <div className="trc-cell-sub">{b.num_adults}A + {b.num_children}C</div>
                      )}
                    </td>
                    <td>
                      {money(b.total_cents, b.currency)}
                      <div className="trc-cell-sub">
                        {paid
                          ? 'paid in full'
                          : b.balance_due_cents > 0
                            ? `${moneyExact(b.balance_due_cents, b.currency)} due (${b.balance_status})`
                            : b.status === 'hold'
                              ? 'awaiting payment'
                              : b.balance_status}
                      </div>
                    </td>
                    <td>
                      {b.provider}
                      {b.source !== 'site' && <div className="trc-cell-sub">{b.source}</div>}
                    </td>
                    <td>
                      <select
                        value={d.status}
                        onChange={(e) => setDraft(b.id, { status: e.target.value })}
                        disabled={b.status === 'hold'}
                        title={b.status === 'hold' ? 'Holds resolve via payment or expiry' : undefined}
                      >
                        {b.status === 'hold' && <option value="hold">hold</option>}
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        value={d.internal_notes}
                        placeholder="—"
                        onChange={(e) => setDraft(b.id, { internal_notes: e.target.value })}
                      />
                    </td>
                    <td className="trc-cell-actions">
                      <button
                        className="trc-btn"
                        disabled={!isDirty(b) || busy === b.id}
                        onClick={() => save(b)}
                      >
                        Save
                      </button>
                      {b.balance_due_cents > 0 &&
                        ['scheduled', 'retry', 'failed'].includes(b.balance_status) &&
                        b.status === 'confirmed' && (
                          <button
                            className="trc-btn trc-btn--ghost"
                            disabled={busy === b.id}
                            onClick={() => chargeBalance(b)}
                          >
                            Charge balance
                          </button>
                        )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {total > items.length && (
        <p className="trc-page-empty">Showing first {items.length} of {total} — refine the search.</p>
      )}
    </div>
  );
}
